import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Route } from 'lucide-react';

import { driversAPI, tripsAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { LEXIQUE } from '../../lib/lexique';
import { formatCurrency } from '../../lib/utils';
import { libelle, nomPays, nomLisible } from '../../lib/libelles';
import { formatDateOuTiret } from '../../lib/formats';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { usePagination } from '../../hooks/usePagination';
import { BarrePagination } from '../ui/pagination-bar';

const dateLabel = (value) => formatDateOuTiret(value);

const statusVariant = (status) => {
  if (status === 'COMPLETED' || status === 'DELIVERED') return 'success';
  if (status === 'CANCELLED') return 'destructive';
  if (status === 'CREATED') return 'muted';
  return 'info';
};

/**
 * Les trajets du camion, du plus récent au plus ancien.
 *
 * Les plaques affichées sont celles **du trajet** (instantané pris au départ):
 * si l'attelage a changé depuis, l'historique garde la citerne qui a vraiment
 * roulé. Un ancien trajet sans instantané retombe sur les plaques actuelles,
 * et le dit.
 *
 * Le montant n'apparaît que pour la direction (`canSeeMoney`).
 */
export default function TruckTripsTable({ truck }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canSeeMoney, hasModule } = useAuth();
  const truckId = truck?.id;

  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!truckId) return;
    setLoading(true);
    setError(false);
    try {
      const [rows, driverPage] = await Promise.all([
        tripsAPI.getAll({ truck_id: truckId }),
        hasModule('drivers')
          ? fetchAllPages((params) => driversAPI.getAllPaged(params)).catch(() => ({ items: [] }))
          : Promise.resolve({ items: [] }),
      ]);
      setTrips(Array.isArray(rows) ? rows : []);
      setDrivers(driverPage?.items || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId]);

  useEffect(() => {
    load();
  }, [load]);
  useRafraichirSur(['trajets', 'camions'], load);

  const driversById = useMemo(() => {
    const map = new Map();
    drivers.forEach((driver) => driver?.id && map.set(driver.id, driver));
    return map;
  }, [drivers]);

  const sorted = useMemo(
    () =>
      [...trips].sort((a, b) =>
        String(b.departure_date || b.created_at || '').localeCompare(
          String(a.departure_date || a.created_at || ''),
        ),
      ),
    [trips],
  );

  const pagination = usePagination(sorted, { cle: 'trajets' });

  if (loading) return <LoadingState shape="table" />;

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Les trajets n’ont pas pu être chargés"
        description="L’historique des trajets de ce camion n’est pas disponible pour le moment."
        actionLabel="Réessayer"
        onAction={load}
      />
    );
  }

  if (sorted.length === 0) {
    return (
      <EmptyState
        variant="empty"
        icon={Route}
        title="Aucun trajet pour ce camion"
        description="Chaque trajet affecté à ce camion apparaîtra ici avec ses plaques au départ, sa destination et son chauffeur."
      />
    );
  }

  return (
    <>
    <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="truck-trips-table">
      <Table stickyFirstColumn>
        <TableHeader>
          <TableRow>
            <TableHead>Départ</TableHead>
            <TableHead>Étape</TableHead>
            <TableHead>{LEXIQUE.plaqueTracteur}</TableHead>
            <TableHead>{LEXIQUE.plaqueCiterne}</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead>Chauffeur</TableHead>
            {canSeeMoney && <TableHead className="text-right">Montant</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagination.lignes.map((trip) => {
            const snapshot = Boolean(trip.tractor_plate || trip.tank_plate);
            const tractor = trip.tractor_plate || truck?.matricule || '—';
            const tank = snapshot ? trip.tank_plate || '—' : truck?.tank_number || '—';
            const driver = trip.driver_id ? driversById.get(trip.driver_id) : null;
            return (
              <TableRow
                key={trip.id}
                className="cursor-pointer"
                onClick={() =>
                  navigate(`/trips/${trip.id}`, {
                    state: { from: `${location.pathname}${location.search}` },
                  })
                }
                data-testid={`truck-trip-${trip.id}`}
              >
                <TableCell>{dateLabel(trip.departure_date || trip.created_at)}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(trip.status)}>
                    {libelle('tripStatus', trip.status, 'Étape à préciser')}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{tractor}</TableCell>
                <TableCell className="font-mono text-xs">
                  {tank}
                  {!snapshot && (
                    <p
                      className="font-sans text-[11px] text-muted-foreground"
                      title="Trajet enregistré avant la mémorisation des plaques"
                    >
                      plaques actuelles
                    </p>
                  )}
                </TableCell>
                <TableCell>{nomPays(trip.destination_country)}</TableCell>
                <TableCell>
                  {driver?.full_name ? nomLisible(driver.full_name) : trip.driver_id ? 'Chauffeur' : '—'}
                </TableCell>
                {canSeeMoney && (
                  <TableCell className="text-right font-mono tabular">
                    {Number(trip.total_amount) > 0 ? formatCurrency(trip.total_amount) : '—'}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
    <BarrePagination pagination={pagination} nom="trajets" />
    </>
  );
}
